import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, ArrowRight, ChevronLeft, ChevronRight, MapPin, Truck, Handshake,
  ClipboardList, Users, Calendar as CalendarIcon, Calculator, ShoppingBag,
  Sparkles, Boxes, FlaskConical, Package as PackageIcon, BookOpen, Scale, Palette, Ruler, Bell,
} from "lucide-react";
import MOShell, { useMOAccess, isWritable, LoadingBlock, NoAccessScreen } from "@/components/masterOrder/MOShell";
import StatusBadge from "@/components/masterOrder/StatusBadge";
import { listOrders, listClients, listAttentionReminders, deliveryLabel, businessLabel, displayOrderNumber } from "@/lib/masterOrderApi";

const HEADER_IMAGE = "/master-order/master-order-header.jpeg";

/* ------------------------------ Header ---------------------------------- */
function CompactHeader() {
  return (
    <section className="w-full max-w-full"
             data-testid="mo-header">
      {/* Owner-approved mobile-only artwork: no baked-in page heading or temporary mask.
          Live text below and desktop's original artwork stay unchanged. */}
      <div className="overflow-hidden rounded-[22px] border border-[#E5D9C8] bg-[#F8F1E6] md:hidden" data-testid="mo-header-mobile">
        <div className="h-[240px] w-full overflow-hidden">
          <img
            src="/master-order/master-order-hero-mobile-20261027.webp"
            alt=""
            width={1200}
            height={732}
            className="block h-[240px] w-full object-cover object-[12%_center]"
            fetchPriority="high"
            loading="eager"
            decoding="async"
            data-testid="mo-header-mobile-artwork"
          />
        </div>
        <div className="px-5 py-6">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#876E5B]">ANOKINA STUDIO</p>
          <h1 className="mt-2 font-serif text-[32px] leading-[1.05] text-[#39291E]">Мастер заказа</h1>
          <p className="mt-3 text-[14px] leading-[1.45] text-[#705B4A]">
            Твой помощник на всём пути — от первого запроса клиента до выдачи заказа.
          </p>
        </div>
      </div>
      {/* Desktop and tablet retain the approved original full-width artwork. */}
      <img
        src={HEADER_IMAGE}
        alt="Anokina Studio — Мастер заказа"
        width={1536}
        height={561}
        className="hidden w-full max-w-full h-auto md:block"
        fetchPriority="high"
        loading="eager"
        decoding="async"
        data-testid="mo-header-image"
      />
    </section>
  );
}

/* -------------------------------- KPI ----------------------------------- */
function KPIRow({ orders, clientsCount }) {
  const today = new Date().toISOString().slice(0, 10);
  const oneWeekLater = new Date(); oneWeekLater.setDate(oneWeekLater.getDate() + 7);
  const weekEnd = oneWeekLater.toISOString().slice(0, 10);

  const todayCount = orders.filter((o) => o.event_date === today).length;
  const weekCount = orders.filter((o) => o.event_date >= today && o.event_date <= weekEnd).length;
  const deliveryCount = orders.filter((o) => o.event_date === today && o.delivery?.type && o.delivery.type !== "pickup").length;
  const pickupCount = orders.filter((o) => o.event_date === today && o.delivery?.type === "pickup").length;
  const activeOrders = orders.filter((o) => ["request","calculation","offer","prepayment_received","in_production","ready"].includes(o.business_status)).length;

  const items = [
    { id: "today",  label: "На сегодня",     big: String(todayCount), sub: `${deliveryCount} доставок · ${pickupCount} самовывоз`, Icon: ClipboardList },
    { id: "week",   label: "На этой неделе", big: String(weekCount),  sub: "ближайшие 7 дней",                                     Icon: CalendarIcon },
    { id: "active", label: "В работе",       big: String(activeOrders), sub: "активных заказов",                                   Icon: Calculator },
    { id: "clients",label: "Клиенты",        big: String(clientsCount), sub: "в вашей базе",                                       Icon: Users },
  ];
  return (
    <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="mo-kpi-row">
      {items.map(({ id, label, big, sub, Icon }) => (
        <div key={id} className="rounded-xl bg-white flex items-center gap-3.5 px-4 py-4"
             style={{ boxShadow: "0 1px 0 rgba(74,55,40,0.06), inset 0 0 0 1px #EFE4D0" }}
             data-testid={`mo-kpi-${id}`}>
          <div className="w-11 h-11 rounded-full grid place-items-center shrink-0" style={{ background: "#F5EBD8", color: "#8B6F4B" }}>
            <Icon size={18} strokeWidth={1.6} />
          </div>
          <div className="min-w-0">
            <div className="text-[10.5px] tracking-[0.22em] uppercase" style={{ color: "#8B6F4B" }}>{label}</div>
            <div className="mt-0.5 leading-none tabular-nums" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, color: "#2A1D14" }}>
              {big}
            </div>
            <div className="text-[11.5px] mt-1 truncate" style={{ color: "#7A6A55" }}>{sub}</div>
          </div>
        </div>
      ))}
    </section>
  );
}

/* --------------------------- Order card + upcoming ---------------------- */
function fmtDate(iso) { if (!iso) return "—"; const [y,m,d] = iso.split("-"); return `${d}.${m}`; }

function OrderCard({ o, client, isMobileHidden, onOpen, onCost }) {
  const DelIcon = o.delivery?.type && o.delivery.type !== "pickup" ? Truck : Handshake;
  return (
    <article
      className={`rounded-xl bg-white p-4 flex flex-col gap-2.5 transition hover:-translate-y-0.5 ${isMobileHidden ? "hidden sm:flex" : ""}`}
      style={{ boxShadow: "0 1px 0 rgba(74,55,40,0.06), inset 0 0 0 1px #EFE4D0" }}
      data-testid={`mo-order-card-${o.id}`}
    >
      <div className="flex items-center justify-between text-[10.5px]" style={{ color: "#8B6F4B" }}>
        <span className="tracking-[0.18em] uppercase">№ {displayOrderNumber(o.order_number)}</span>
        <span className="px-2 py-0.5 rounded-full" style={{ background: "#F5EBD8", color: "#5B4326" }}>
          {fmtDate(o.event_date)}{o.event_time ? ` · ${o.event_time.slice(0,5)}` : ""}
        </span>
      </div>
      <div>
        <div className="text-[11px] tracking-[0.22em] uppercase" style={{ color: "#8B6F4B" }}>
          {client?.name || "Клиент"}
        </div>
        <div className="mt-0.5 text-[16px] leading-snug truncate" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
          {o.event_type || "Заказ"}
        </div>
        <div className="text-[11.5px] mt-0.5 truncate" style={{ color: "#7A6A55" }}>{o.wishes || "—"}</div>
      </div>
      <div className="grid grid-cols-1 gap-y-1 text-[11.5px]" style={{ color: "#5B4326" }}>
        {o.delivery?.type && (
          <div className="flex items-center gap-1.5 truncate">
            <DelIcon size={12} strokeWidth={1.7} /> {deliveryLabel(o.delivery.type)}
            {o.delivery.address && (
              <>
                <MapPin size={11} strokeWidth={1.6} className="ml-1 opacity-60" />
                <span className="truncate opacity-80">{o.delivery.address}</span>
              </>
            )}
          </div>
        )}
        {o.design_notes && (
          <div className="flex items-center gap-1.5 opacity-80 truncate">
            <Sparkles size={11} strokeWidth={1.6} /> {o.design_notes}
          </div>
        )}
      </div>
      <div className="flex items-end justify-between pt-2 mt-auto border-t" style={{ borderColor: "#EFE4D0" }}>
        <div>
          <div className="text-[16px] tabular-nums leading-none" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
            {o.budget != null ? `${o.budget} ${o.currency_code}` : <span style={{ color: "#B9A692" }}>—</span>}
          </div>
        </div>
        <StatusBadge status={o.business_status} />
      </div>
      <div className="flex gap-1.5">
        <button onClick={() => onOpen(o)}
                className="flex-1 h-8 rounded-full text-[11.5px] transition hover:brightness-110"
                style={{ background: "#4A3728", color: "#F7EFE0", boxShadow: "0 6px 14px -10px rgba(74,55,40,0.55)" }}
                data-testid={`mo-order-open-${o.id}`}>
          Открыть
        </button>
        <button onClick={() => onCost(o)}
                className="w-8 h-8 rounded-full grid place-items-center transition hover:bg-[#FBF5EA]"
                style={{ boxShadow: "inset 0 0 0 1px #D9C6A6", color: "#4A3728", background: "#FFFFFF" }}
                aria-label="Калькуляция"
                data-testid={`mo-order-cost-${o.id}`}>
          <Calculator size={13} strokeWidth={1.7} />
        </button>
      </div>
    </article>
  );
}

function UpcomingOrders({ orders, clients, onOpen, onCost, onAll, canWrite, onNew }) {
  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  return (
    <section data-testid="mo-upcoming">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-[19px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
          Ближайшие заказы
        </h2>
        <button onClick={onAll} className="text-[12.5px] inline-flex items-center gap-1 hover:underline"
                style={{ color: "#8B6F4B" }} data-testid="mo-upcoming-all">
          Все заказы <ArrowRight size={13} />
        </button>
      </div>
      {orders.length === 0 ? (
        <div className="rounded-xl bg-white p-8 text-center" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-upcoming-empty">
          <div className="text-[18px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Заказов пока нет</div>
          <p className="text-[12.5px] mt-1.5" style={{ color: "#8B6F4B" }}>Создайте первый заказ — он появится здесь и на экране «Заказы».</p>
          {canWrite && (
            <button onClick={onNew}
                    className="mt-4 h-11 px-5 rounded-xl text-[13px] inline-flex items-center gap-2 bg-[#D8D3CD] text-[#4A3728] md:bg-[#4A3728] md:text-[#F7EFE0]">
              <Plus size={14} strokeWidth={1.8} /> Новый заказ
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile (<sm): compact list — up to 3 upcoming orders, one row each.
             Row layout: «№N · Client» top line, «date · time · status» sub line.
             Tapping the row opens the order. Delivery, budget, big buttons hidden. */}
          <ul className="sm:hidden rounded-xl bg-white divide-y"
              style={{ boxShadow: "0 1px 0 rgba(74,55,40,0.06), inset 0 0 0 1px #EFE4D0",
                       borderColor: "#EFE4D0" }}
              data-testid="mo-upcoming-mobile-list">
            {orders.slice(0, 3).map((o) => (
              <li key={o.id}>
                <button
                  onClick={() => onOpen(o)}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 min-w-0 transition active:bg-[#FBF5EA]"
                  data-testid={`mo-upcoming-mobile-row-${o.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-[13px] leading-tight min-w-0"
                         style={{ color: "#2A1D14" }}>
                      <span className="tabular-nums shrink-0">№ {displayOrderNumber(o.order_number)}</span>
                      <span className="opacity-50 shrink-0">·</span>
                      <span className="truncate">{clientMap[o.client_id]?.name || "Клиент"}</span>
                    </div>
                    <div className="mt-0.5 text-[11.5px] flex items-center gap-1.5 min-w-0 tabular-nums"
                         style={{ color: "#8B6F4B" }}>
                      <span className="shrink-0">
                        {fmtDate(o.event_date)}{o.event_time ? ` · ${o.event_time.slice(0,5)}` : ""}
                      </span>
                      <span className="opacity-50 shrink-0">·</span>
                      <span className="truncate">{businessLabel(o.business_status)}</span>
                    </div>
                  </div>
                  <ChevronRight size={14} strokeWidth={1.7} className="shrink-0 opacity-60"
                                style={{ color: "#8B6F4B" }} />
                </button>
              </li>
            ))}
          </ul>

          {/* Desktop (≥sm): existing card grid — unchanged. */}
          <div className="hidden sm:grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {orders.slice(0, 3).map((o) => (
              <OrderCard key={o.id} o={o} client={clientMap[o.client_id]}
                         isMobileHidden={false} onOpen={onOpen} onCost={onCost} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

/* ------------------------------ Navigation groups ---------------------- */
/* Compact, mobile-first grouped navigation. Replaces the previous oversized
   «Быстрые действия» grid. Two clearly labelled groups expose every daily-work
   module + directory in Master Order so the Overview stops being a hidden
   quest — especially important on mobile where the sidebar is not visible. */
function NavGroups({ onNav, canWrite }) {
  const workItems = [
    { id: "new-order",  label: "Новый заказ",  Icon: Plus,           primary: true, disabled: !canWrite,
      to: "/master-order/orders/new" },
    { id: "orders",     label: "Заказы",       Icon: ClipboardList,  to: "/master-order/orders" },
    { id: "clients",    label: "Клиенты",      Icon: Users,          to: "/master-order/clients" },
    { id: "recipe-recalc", label: "Расчёты",   Icon: Scale,          to: "/master-order/recipe-recalc" },
    { id: "product-cost",  label: "Себестоимость изделия", Icon: Calculator, to: "/master-order/product-cost" },
    { id: "purchases",  label: "Список покупок", Icon: ShoppingBag,  to: "/master-order/purchases" },
    { id: "production", label: "Производство", Icon: Boxes,          to: "/master-order/production" },
    { id: "brand-studio", label: "Brand Studio", Icon: Palette,      to: "/brand-studio" },
  ];
  const catalogItems = [
    { id: "ingredients",      label: "Ингредиенты", Icon: FlaskConical, to: "/master-order/ingredients" },
    { id: "packaging-items",  label: "Упаковка",    Icon: PackageIcon,  to: "/master-order/packaging-items" },
    { id: "recipes",          label: "Рецептуры",   Icon: BookOpen,     to: "/master-order/recipes" },
    { id: "measure-directory", label: "Справочник мер", Icon: Ruler, to: "/master-order/measure-directory" },
    { id: "calendar",         label: "Календарь",   Icon: CalendarIcon, to: "/master-order/calendar" },
  ];
  return (
    <div className="space-y-3" data-testid="mo-nav-groups">
      <NavGroup title="Работа" items={workItems} onNav={onNav} testId="mo-nav-work" />
      <NavGroup title="Справочники и инструменты" items={catalogItems} onNav={onNav} testId="mo-nav-catalog" />
    </div>
  );
}

function NavGroup({ title, items, onNav, testId }) {
  return (
    <div className="rounded-xl bg-white p-3.5 md:p-4"
         style={{ boxShadow: "0 1px 0 rgba(74,55,40,0.06), inset 0 0 0 1px #EFE4D0" }}
         data-testid={testId}>
      <div className="mb-2 text-[10.5px] tracking-[0.18em] uppercase" style={{ color: "#8B6F4B" }}
           data-testid={`${testId}-title`}>
        {title}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {items.map(({ id, label, Icon, primary, disabled, to, badge }) => (
          <button
            key={id}
            onClick={disabled ? undefined : () => onNav(to)}
            disabled={disabled}
            className={"rounded-lg h-14 md:h-[60px] px-3 flex items-center gap-2.5 text-left transition disabled:opacity-50 hover:-translate-y-[1px] disabled:hover:translate-y-0" +
              (primary ? " bg-[#D8D3CD] text-[#4A3728] md:bg-[#4A3728] md:text-[#F7EFE0]" : "")}
            style={
              primary
                ? { boxShadow: "0 6px 18px -12px rgba(74,55,40,0.55)" }
                : { background: "#FBF5EA", color: "#2A1D14", boxShadow: "inset 0 0 0 1px #EFE4D0" }
            }
            data-testid={`mo-overview-nav-${id}`}
          >
            <div className={"w-8 h-8 rounded-full grid place-items-center shrink-0" +
                   (primary ? " bg-[#F2EEEA] text-[#4A3728] md:bg-[#C7A574]/30 md:text-[#F7EFE0]" : "")}
                 style={primary ? undefined : { background: "#F5EBD8", color: "#8B6F4B" }}>
              <Icon size={14} strokeWidth={1.8} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] leading-tight truncate">{label}</div>
              {badge && (
                <div className="text-[9.5px] tracking-[0.14em] uppercase mt-0.5 truncate"
                     style={{ color: "#8B6F4B" }}
                     data-testid={`mo-overview-nav-${id}-badge`}>
                  {badge}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function AttentionReminders({ reminders, orders, clients, onOpen }) {
  const orderMap = useMemo(() => Object.fromEntries(orders.map((order) => [order.id, order])), [orders]);
  const clientMap = useMemo(() => Object.fromEntries(clients.map((client) => [client.id, client])), [clients]);
  const today = new Date().toISOString().slice(0, 10);
  return (
    <section className="min-w-0" data-testid="mo-attention-reminders">
      <div className="mb-3 flex items-center gap-2">
        <Bell size={15} className="text-[#8B6F4B]" />
        <h2 className="font-['Cormorant_Garamond',serif] text-[19px] text-[#2A1D14]">Требует внимания</h2>
      </div>
      {reminders.length === 0 ? (
        <div className="rounded-xl bg-white p-4 text-[12.5px] text-[#8B6F4B] shadow-[inset_0_0_0_1px_#EFE4D0]">На сегодня и просроченных напоминаний нет.</div>
      ) : (
        <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
          {reminders.map((reminder) => {
            const order = orderMap[reminder.order_id];
            const client = order ? clientMap[order.client_id] : null;
            const overdue = reminder.reminder_date < today;
            return (
              <button key={reminder.id} type="button" onClick={() => order && onOpen(order)}
                className="min-w-0 rounded-xl bg-white p-4 text-left shadow-[inset_0_0_0_1px_#EFE4D0] transition hover:bg-[#FFFDF8]">
                <div className={`text-[10.5px] uppercase tracking-[0.16em] ${overdue ? "text-[#9B4335]" : "text-[#8B6F4B]"}`}>
                  {overdue ? "Просрочено" : "Сегодня"} · {reminder.reminder_date}{reminder.reminder_time ? ` · ${reminder.reminder_time.slice(0, 5)}` : ""}
                </div>
                <div className="mt-1 break-words text-[13px] leading-snug text-[#2A1D14]">{reminder.action}</div>
                <div className="mt-2 truncate text-[11.5px] text-[#8B6F4B]">
                  {order ? `Заказ № ${displayOrderNumber(order.order_number)}` : "Заказ"}{client?.name ? ` · ${client.name}` : ""}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* --------------------------- Reminders / schedule (light) --------------- */
function RightHelpers({ orders, clients }) {
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = orders.filter((o) => o.event_date >= today).slice(0, 4);
  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  return (
    <div className="rounded-xl bg-white p-4" style={{ boxShadow: "0 1px 0 rgba(74,55,40,0.06), inset 0 0 0 1px #EFE4D0" }} data-testid="mo-schedule">
      <div className="flex items-center justify-between mb-3">
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, color: "#2A1D14" }}>Ближайшие даты</div>
      </div>
      {upcoming.length === 0 ? (
        <div className="text-[12.5px]" style={{ color: "#8B6F4B" }}>Ближайших заказов нет.</div>
      ) : (
        <div className="space-y-2.5">
          {upcoming.map((o) => (
            <div key={o.id} className="flex items-start gap-3">
              <div className="w-14 shrink-0 text-[11px] tabular-nums pt-0.5" style={{ color: "#8B6F4B" }}>
                {fmtDate(o.event_date)}{o.event_time ? ` ${o.event_time.slice(0,5)}` : ""}
              </div>
              <div className="min-w-0 flex-1 border-l pl-3" style={{ borderColor: "#EFE4D0" }}>
                <div className="text-[12.5px] leading-tight truncate" style={{ color: "#2A1D14" }}>
                  № {displayOrderNumber(o.order_number)} · {clientMap[o.client_id]?.name || "—"}
                </div>
                <div className="text-[10.5px] mt-0.5" style={{ color: "#8B6F4B" }}>
                  {businessLabel(o.business_status)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------- Page --------------------------------- */
export default function MasterOrderOverview() {
  const nav = useNavigate();
  return (
    <MOShell active="overview" title="Обзор" subtitle="Мастер заказа">
      <Inner nav={nav} />
    </MOShell>
  );
}

function Inner({ nav }) {
  const { mode, loading: aLoad } = useMOAccess();
  const canWrite = isWritable(mode);
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [attentionReminders, setAttentionReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (aLoad || mode === "no_access" || mode === "demo") return;
    (async () => {
      setLoading(true);
      try {
        const today = new Date().toISOString().slice(0, 10);
        const [os, cs, reminders] = await Promise.all([listOrders(), listClients(), listAttentionReminders(today)]);
        // sort by event_date asc
        os.sort((a, b) => `${a.event_date}T${a.event_time || ""}`.localeCompare(`${b.event_date}T${b.event_time || ""}`));
        setOrders(os);
        setClients(cs);
        setAttentionReminders(reminders);
      } catch (_) {}
      finally { setLoading(false); }
    })();
  }, [aLoad, mode]);

  const upcoming = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return orders.filter((o) => o.event_date >= today);
  }, [orders]);

  if (aLoad) return <LoadingBlock />;
  if (mode === "no_access" || mode === "demo") return <NoAccessScreen />;

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-5 md:gap-6">
        <div className="min-w-0 space-y-4 md:space-y-6">
          <CompactHeader />
          {loading ? <LoadingBlock /> : <KPIRow orders={orders} clientsCount={clients.length} />}
          {!loading && <AttentionReminders reminders={attentionReminders} orders={orders} clients={clients}
            onOpen={(order) => nav(`/master-order/orders/${order.id}`)} />}
          {loading ? null : (
            <UpcomingOrders
              orders={upcoming}
              clients={clients}
              canWrite={canWrite}
              onNew={() => nav("/master-order/orders/new")}
              onOpen={(o) => nav(`/master-order/orders/${o.id}`)}
              onCost={(o) => nav(`/master-order/orders/${o.id}`)}
              onAll={() => nav("/master-order/orders")}
            />
          )}
        </div>
        <aside className="space-y-4 min-w-0" data-testid="mo-right-column">
          <NavGroups canWrite={canWrite} onNav={nav} />
          {/* «Ближайшие даты» — desktop only; на мобиле есть компактный список «Ближайшие заказы». */}
          <div className="hidden md:block">
            <RightHelpers orders={upcoming} clients={clients} />
          </div>
        </aside>
      </div>
    </div>
  );
}
